/**
 * Modular Reports Component
 * Main orchestrator for the modularized reports system
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CalendarIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Sub-module imports
import { useReportsData } from './reports.data';
import { useReportGenerator, REPORT_FORMATS, REPORT_TYPE_DEFINITIONS, getAllReportTypes } from './reports.generators';
import { useReportExport, ExportControls } from './reports.export';
import { useAnalytics, InsightsPanel } from './reports.analytics';
import { useScheduling } from './reports.scheduling';
import ServiceAttendeeReportWrapper from './reports.generators/components/ServiceAttendeeReportWrapper';

const ModularReports = ({ 
  defaultReportType = 'daily-log',
  layout = 'default',
  showAnalytics = true,
  showScheduling = true,
  className = '' 
}) => {
  // Core state
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedCategory, setSelectedCategory] = useState('rental');
  const [selectedReportType, setSelectedReportType] = useState(defaultReportType);
  const [reportParameters, setReportParameters] = useState({});
  const [currentReport, setCurrentReport] = useState(null);
  const [services, setServices] = useState([]);

  // Sub-module hooks
  const { 
    generateReport: fetchReportData, 
    loading: dataLoading, 
    error: dataError 
  } = useReportsData();
  
  const { 
    generateHTMLReport, 
    generating, 
    error: generateError,
    lastGenerated 
  } = useReportGenerator();
  
  const { 
    exportReport, 
    exporting, 
    error: exportError 
  } = useReportExport();
  
  const { 
    analyzeReport, 
    analytics, 
    analyzing, 
    error: analyticsError 
  } = useAnalytics();
  
  const { 
    schedules, 
    createSchedule, 
    loading: schedulingLoading,
    error: schedulingError 
  } = useScheduling();

  // Get available report types by category
  const allReportTypes = getAllReportTypes();
  const reportsByCategory = {
    rental: allReportTypes.filter(r => r.category === 'rental'),
    service: allReportTypes.filter(r => r.category === 'service'),
    general: allReportTypes.filter(r => r.category === 'general')
  };
  const currentCategoryReports = reportsByCategory[selectedCategory] || [];

  // Initialize default parameters when report type changes
  // Fetch services for dropdown options
  useEffect(() => {
    const fetchServices = async () => {
      try {
        console.log('🔍 Fetching services for reports dropdown...');
        const response = await axios.get('/api/inventory/services', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('🔍 Services API response:', response.data);

        if (response.data.success && response.data.data) {
          console.log('🔍 Setting services:', response.data.data.length, 'services found');
          setServices(response.data.data);
        } else if (response.data && Array.isArray(response.data)) {
          // Handle case where data is directly an array
          console.log('🔍 Setting services (direct array):', response.data.length, 'services found');
          setServices(response.data);
        } else {
          console.warn('🔍 Unexpected response structure:', response.data);
          setServices([]);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error.response?.status, error.response?.data || error.message);
        setServices([]);
      }
    };

    fetchServices();
  }, []);

  // Reset parameters when report type changes (without auto-filling)
  useEffect(() => {
    setReportParameters({});
  }, [selectedReportType]);

  // Generate report workflow
  const handleGenerateReport = useCallback(async () => {
    try {
      // Step 1: Fetch report data
      const reportData = await fetchReportData(selectedReportType, reportParameters);
      if (!reportData) return;

      // Step 2: Generate HTML report
      const htmlReport = await generateHTMLReport(selectedReportType, reportData);
      if (!htmlReport) return;

      setCurrentReport({
        type: selectedReportType,
        parameters: reportParameters,
        data: reportData,
        content: htmlReport.content,
        generatedAt: new Date().toISOString()
      });

      // Step 3: Generate analytics if enabled
      if (showAnalytics) {
        await analyzeReport(selectedReportType, reportData, reportParameters);
      }

    } catch (error) {
      console.error('Report generation failed:', error);
    }
  }, [selectedReportType, reportParameters, fetchReportData, generateHTMLReport, analyzeReport, showAnalytics]);

  // Update report parameter
  const updateParameter = useCallback((key, value) => {
    setReportParameters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Schedule current report
  const handleScheduleReport = useCallback(async (scheduleConfig) => {
    if (!currentReport) return;

    const config = {
      ...scheduleConfig,
      reportType: currentReport.type,
      parameters: currentReport.parameters
    };

    const result = await createSchedule(config);
    return result;
  }, [currentReport, createSchedule]);

  // Check for any errors
  const hasErrors = !!(dataError || generateError || exportError || analyticsError || schedulingError);
  const isLoading = dataLoading || generating || exporting || analyzing || schedulingLoading;

  // Tab configuration
  const tabs = [
    { id: 'generate', name: 'Generate', icon: DocumentTextIcon },
    { id: 'analytics', name: 'Analytics', icon: ChartBarIcon, show: showAnalytics },
    { id: 'export', name: 'Export', icon: ArrowPathIcon },
    { id: 'schedule', name: 'Schedule', icon: CalendarIcon, show: showScheduling },
    { id: 'settings', name: 'Settings', icon: Cog6ToothIcon }
  ].filter(tab => tab.show !== false);

  return (
    <div className={`modular-reports bg-white rounded-lg shadow ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Reports</h1>
            <p className="text-sm text-gray-600 mt-1">
              Generate, analyze, and manage reports with advanced analytics
            </p>
          </div>

          {/* Status Indicators */}
          <div className="flex items-center space-x-2">
            {hasErrors && (
              <div className="flex items-center text-red-600 text-sm">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                Error
              </div>
            )}
            {isLoading && (
              <div className="flex items-center text-blue-600 text-sm">
                <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                Processing...
              </div>
            )}
            {schedules.length > 0 && (
              <div className="text-sm text-gray-600">
                {schedules.filter(s => s.enabled).length} scheduled
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Error Display */}
        {hasErrors && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error occurred
                </h3>
                <div className="text-sm text-red-700 mt-1">
                  {dataError || generateError || exportError || analyticsError || schedulingError}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'generate' && (
          <div className="space-y-6">
            {/* Report Configuration */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Report Configuration
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Report Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      // Auto-select first report in new category
                      const newCategoryReports = reportsByCategory[e.target.value];
                      if (newCategoryReports && newCategoryReports.length > 0) {
                        setSelectedReportType(newCategoryReports[0].id);
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  >
                    <option value="rental">Rental Reports</option>
                    <option value="service">Service Reports</option>
                    <option value="general">General Reports</option>
                  </select>
                </div>

                {/* Report Type Selection (filtered by category) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Type
                  </label>
                  <select
                    value={selectedReportType}
                    onChange={(e) => setSelectedReportType(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  >
                    {currentCategoryReports.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {REPORT_TYPE_DEFINITIONS[selectedReportType]?.description}
                  </p>
                </div>

                {/* Service Selection (only for service-related reports) */}
                {REPORT_TYPE_DEFINITIONS[selectedReportType]?.parameters?.serviceId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specific Service (Optional)
                    </label>
                    <select
                      value={reportParameters.serviceId || ''}
                      onChange={(e) => updateParameter('serviceId', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      <option value="">Select a service...</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Other Parameters (exclude serviceId since it's handled separately) */}
                {REPORT_TYPE_DEFINITIONS[selectedReportType]?.parameters &&
                  Object.entries(REPORT_TYPE_DEFINITIONS[selectedReportType].parameters)
                    .filter(([key]) => key !== 'serviceId')
                    .map(([key, param]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {param.label}
                        {param.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {param.type === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={reportParameters[key] || param.defaultValue || false}
                          onChange={(e) => updateParameter(key, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          disabled={isLoading}
                        />
                      ) : (
                        <input
                          type={param.type}
                          value={reportParameters[key] || ''}
                          onChange={(e) => updateParameter(key, e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={isLoading}
                        />
                      )}
                    </div>
                  ))}

                {/* Generate Button */}
                <div className="flex items-end">
                  <button
                    onClick={handleGenerateReport}
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="h-4 w-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Report Display - Full width below configuration */}
            {currentReport && (
              <div className="bg-white border border-gray-300 rounded-lg">
                <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    {REPORT_TYPE_DEFINITIONS[currentReport.type]?.name || currentReport.type}
                  </h3>

                  <ExportControls
                    content={currentReport.content}
                    reportType={currentReport.type}
                    parameters={currentReport.parameters}
                    showLabels={false}
                    buttonSize="sm"
                  />
                </div>

                <div className="p-4">
                  {currentReport.type === 'service-attendees' ? (
                    <ServiceAttendeeReportWrapper
                      reportType={currentReport.type}
                      parameters={currentReport.parameters}
                      className="prose max-w-none"
                    />
                  ) : (
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: currentReport.content }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Empty state when no report */}
            {!currentReport && (
              <div className="bg-white border border-gray-300 rounded-lg">
                <div className="text-center py-16">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">
                    Configure and generate a report to see it here.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && showAnalytics && (
          <div>
            <InsightsPanel 
              analytics={analytics}
              className="max-w-none"
            />
          </div>
        )}

        {activeTab === 'export' && currentReport && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Export Options
              </h3>
              
              <ExportControls
                content={currentReport.content}
                reportType={currentReport.type}
                parameters={currentReport.parameters}
                showLabels={true}
                buttonSize="md"
                orientation="horizontal"
                className="relative"
              />
            </div>
          </div>
        )}

        {activeTab === 'schedule' && showScheduling && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Scheduled Reports
              </h3>
              
              <div className="space-y-4">
                {schedules.length > 0 ? (
                  schedules.map(schedule => (
                    <div key={schedule.id} className="bg-white border border-gray-200 rounded-md p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {schedule.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {REPORT_TYPE_DEFINITIONS[schedule.reportType]?.name} - {schedule.schedule.frequency}
                          </p>
                          <p className="text-xs text-gray-500">
                            Next run: {new Date(schedule.nextRun).toLocaleString()}
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            schedule.enabled 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {schedule.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No scheduled reports yet.</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Schedule reports to run automatically at specified intervals.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Report Settings
              </h3>
              
              <div className="space-y-4 text-sm text-gray-600">
                <p>• Analytics: {showAnalytics ? 'Enabled' : 'Disabled'}</p>
                <p>• Scheduling: {showScheduling ? 'Enabled' : 'Disabled'}</p>
                <p>• Default Report: {REPORT_TYPE_DEFINITIONS[defaultReportType]?.name}</p>
                <p>• Layout: {layout}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModularReports;